import loadResourceString from "@core/Util/resources";
import {COLORS} from "@modules/JourneyBuilder/constants";
import brickTypes, { parentTypes } from "@modules/JourneyBuilder/Bricks/brick-types";

import {
    withNamedPermanentConnection,
    withUnconnectedRegularConnectionAtAllTimes,
    withCustomizedOutgoingConnections
} from "@modules/JourneyBuilder/Bricks/brick-initializer-helpers";
import alert from "@modules/JourneyBuilder/Util/alert";

/**
 * Invoke a callback function every time the brick config is set
 * @param {T} target
 * @param {String} propertyName 
 * @param {function()} callback
 * @template T
 */
const withReactiveSetter = (
  target,
  propertyName,
  callback
) => {
    const targetPropDef = Object.getOwnPropertyDescriptor(target, propertyName);
    
    //Don't overwrite getter when getter has already been defined
    if(!targetPropDef.set){
        let _propVal = target[propertyName];
        Object.defineProperty(target, propertyName, {
            configurable: true,
            get(){ 
                return _propVal 
            },
            set(val) {
                _propVal = val;
                callback();
                return val;
            }
        });
    } else {
        Object.defineProperty(target, propertyName, {
            ...targetPropDef,
            set() {
                const retVal = targetPropDef.set.apply(this, arguments);
                callback();
                return retVal;
            }
        });   
    }
}

/**
 * https://datanext.atlassian.net/browse/DPN-3091?focusedCommentId=55097
 * As we don't have statistics for Google/Facebook touchpoints yet, we don't support incoming connections for those
 * @param {JourneyBrick} brick
 */
const withValidGoalConnections = brick => {
    const supportsGoalConnection = () => !brick.config ||
      !brick.config.touchpointParentType ||
      ![parentTypes.google, parentTypes.fb].includes(brick.config.touchpointParentType);

    /** @type {JourneyBrickConnection[]} connections */
    const deleteUnsupportedGoalConnections = (connections) => {
        connections.forEach(connection => connection.delete());

        if(connections.length) {
            alert(
              loadResourceString('JourneyBuilder_TouchPoint_GoalConnectionNotAllowed_Title'),
              loadResourceString('JourneyBuilder_TouchPoint_GoalConnectionNotAllowed_Body')
            );
        }
    }

    withReactiveSetter(brick, 'config', () => {
        //Remove all invalid goal connections on type change
        if(!supportsGoalConnection()){
            const connectionsToRemove = brick.outgoingConnections
              .filter(connection => connection.isConnected && connection.destinationBrick.type === brickTypes.goal);

            deleteUnsupportedGoalConnections(connectionsToRemove);
        }
    });

    const _registerConnection = brick.registerConnection;
    brick.registerConnection = function(connection) {
        withReactiveSetter(connection, 'destinationBrick', () => {
            //Prevent invalid goal connection by severin
            if(!supportsGoalConnection() && connection.destinationBrick.type === brickTypes.goal) {
                deleteUnsupportedGoalConnections([connection]);
            }
        });

        return _registerConnection.apply(this, arguments);
    }
}

/**
 * Initialize the given touchpoint brick
 * @param {JourneyBrick} brick
 */
const initialize = brick => {
    /**
     * Wrap isActive function also checking whether touchpoint config contains a type
     * @param {JourneyBrick|JourneyBrickConnection} brickOrConnection
     */
    const withBrickBasedTouchpointIsActive = brickOrConnection => {
        const _isActive = brickOrConnection.isActive;
        brickOrConnection.isActive = function(){
            //Theoretically it should never be possible for our brick to have a touchPointTypeId without a touchpointParentType
            //Touchpoint bricks can only be used once a type has been configured
            //until a type has been selected, we can consider them inactive
            if(!brick.config.touchpointParentType || !brick.config.touchPointTypeId){
                return false;
            }
            
            //Config should be checked prior to invoking _isActive
            //This because our config check should be a lot less computationally intensive
            return _isActive.apply(this, arguments);
        }
    }

    withValidGoalConnections(brick);

    withBrickBasedTouchpointIsActive(brick);
    withCustomizedOutgoingConnections(brick, connection => withBrickBasedTouchpointIsActive(connection));
    
    withNamedPermanentConnection(brick, {
        connectionIdProperty: 'outflowConnectionId',
        configureConnection: connection => {
            connection.ui.cssClass = 'jc-outflow';
            connection.ui.backgroundColor = COLORS.BRICK_ENDPOINT_OUTFLOW;
            connection.ui.tooltip.content = loadResourceString('JourneyComposer_Shared_OutflowConnection_TooltipContent');
        }
    });

    //Our initializeWithConnection will not add a connection when our outflow port is present
    //ensure at least a single regular outgoing port is present
    withUnconnectedRegularConnectionAtAllTimes(brick, {
        isRegularConnection: connection => connection.id !== brick.config.outflowConnectionId
    });
}

export { 
    initialize 
};